import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    LinearProgress,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
    BulkSessionJobStatus,
    BulkSessionRunStatusItem,
    BulkSessionRunStatusResponse,
    enqueueBulkCreateSessions,
    fetchImportedPendingSessions,
    getBulkCreateSessionsStatus,
    PendingSessionWorker,
} from '../api/workersApi';

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = new Set<BulkSessionJobStatus>(['COMPLETED', 'ERROR']);

function statusColor(s: BulkSessionJobStatus): 'default' | 'info' | 'warning' | 'success' | 'error' {
    if (s === 'COMPLETED') return 'success';
    if (s === 'ERROR') return 'error';
    if (s === 'AWAITING_OPTIMA_AI') return 'warning';
    if (s === 'IN_PROGRESS') return 'info';
    return 'default';
}

export default function PendingSessionsPage() {
    const [workers, setWorkers] = useState<PendingSessionWorker[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [enqueueing, setEnqueueing] = useState(false);
    const [run, setRun] = useState<BulkSessionRunStatusResponse | null>(null);
    const [toast, setToast] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await fetchImportedPendingSessions();
            setWorkers(res.data || []);
        } catch (err: any) {
            setLoadError(err?.message ?? 'Failed to load pending-session workers');
            setWorkers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const allSelected = workers.length > 0 && workers.every((w) => selected.has(w.workerId));
    const someSelected = workers.some((w) => selected.has(w.workerId)) && !allSelected;

    const togglePage = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                for (const w of workers) next.delete(w.workerId);
            } else {
                for (const w of workers) next.add(w.workerId);
            }
            return next;
        });
    };

    const toggleOne = (workerId: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(workerId)) next.delete(workerId);
            else next.add(workerId);
            return next;
        });
    };

    const handleEnqueue = async () => {
        if (selected.size === 0) return;
        setEnqueueing(true);
        try {
            const res = await enqueueBulkCreateSessions(Array.from(selected));
            setToast(`Queued ${res.queued} workers (syncRunId=${res.syncRunId.slice(0, 8)}…)`);
            setSelected(new Set());
            await load();
            startPolling(res.syncRunId);
        } catch (err: any) {
            setToast(err?.message ?? 'Bulk create-sessions enqueue failed');
        } finally {
            setEnqueueing(false);
        }
    };

    const startPolling = useCallback((syncRunId: string) => {
        stopPolling();
        const tick = async () => {
            try {
                const status = await getBulkCreateSessionsStatus(syncRunId);
                setRun(status);
                const allTerminal = status.items.every((i) => TERMINAL_STATUSES.has(i.status));
                if (allTerminal) {
                    stopPolling();
                    await load();
                }
            } catch (err: any) {
                setToast(`Status poll failed: ${err?.message ?? err}`);
                stopPolling();
            }
        };
        void tick();
        pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    }, [load]);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    useEffect(() => () => stopPolling(), []);

    const progress = useMemo(() => {
        if (!run) return null;
        const total = run.items.length;
        const done = (run.counts.COMPLETED ?? 0) + (run.counts.ERROR ?? 0);
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return { total, done, pct };
    }, [run]);

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h5">Imported workers awaiting sessions</Typography>
                <Stack direction="row" spacing={1}>
                    <Button component={RouterLink} to="/resources/workers/bulk-import" variant="outlined">
                        ← Bulk import
                    </Button>
                    <Button onClick={load} disabled={loading}>
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            )}

            {run && progress && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2">
                                Run {run.syncRunId.slice(0, 8)}… — {progress.done} / {progress.total} done
                            </Typography>
                            <LinearProgress variant="determinate" value={progress.pct} sx={{ mt: 1 }} />
                            <Stack direction="row" spacing={1} mt={1}>
                                <Chip size="small" label={`Queued: ${run.counts.QUEUED ?? 0}`} />
                                <Chip size="small" label={`In progress: ${run.counts.IN_PROGRESS ?? 0}`} color="info" />
                                <Chip size="small" label={`Awaiting AI: ${run.counts.AWAITING_OPTIMA_AI ?? 0}`} color="warning" />
                                <Chip size="small" label={`Completed: ${run.counts.COMPLETED ?? 0}`} color="success" />
                                <Chip size="small" label={`Error: ${run.counts.ERROR ?? 0}`} color="error" />
                            </Stack>
                        </Box>
                    </Stack>
                    <RunDetail items={run.items} />
                </Paper>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="subtitle2">
                        {workers.length} workers without sessions
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        onClick={handleEnqueue}
                        disabled={enqueueing || selected.size === 0}
                    >
                        {enqueueing ? <CircularProgress size={20} /> : `Create sessions for ${selected.size}`}
                    </Button>
                </Stack>
            </Paper>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onChange={togglePage}
                                    disabled={workers.length === 0}
                                />
                            </TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>EasyFlex reg #</TableCell>
                            <TableCell>Imported</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <CircularProgress size={20} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && workers.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No imported workers are missing a session.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            workers.map((w) => {
                                const isSelected = selected.has(w.workerId);
                                return (
                                    <TableRow key={w.workerId} hover selected={isSelected}>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={isSelected} onChange={() => toggleOne(w.workerId)} />
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                component={RouterLink}
                                                to={`/workers/${w.workerId}`}
                                                sx={{ color: 'inherit', textDecoration: 'underline' }}
                                            >
                                                {w.firstName} {w.lastName}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{w.emailAddress ?? '—'}</TableCell>
                                        <TableCell>{w.phoneNumber ?? '—'}</TableCell>
                                        <TableCell>{w.easyflexRegistrationNumber ?? '—'}</TableCell>
                                        <TableCell>
                                            {w.importedAt ? new Date(w.importedAt).toLocaleString() : '—'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={!!toast}
                autoHideDuration={6000}
                onClose={() => setToast(null)}
                message={toast ?? ''}
            />
        </Box>
    );
}

function RunDetail({ items }: { items: BulkSessionRunStatusItem[] }) {
    if (items.length === 0) return null;
    return (
        <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Worker</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Strategy</TableCell>
                        <TableCell>Suggestions</TableCell>
                        <TableCell>Error</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((it) => (
                        <TableRow key={it.workerId}>
                            <TableCell>
                                <Box
                                    component={RouterLink}
                                    to={`/workers/${it.workerId}`}
                                    sx={{ color: 'inherit', textDecoration: 'underline' }}
                                >
                                    {it.workerId.slice(0, 8)}…
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Chip label={it.status} size="small" color={statusColor(it.status)} />
                            </TableCell>
                            <TableCell>{it.strategy ?? '—'}</TableCell>
                            <TableCell>{it.suggestionsApplied}</TableCell>
                            <TableCell>
                                <Typography variant="caption" color="error">
                                    {it.errorMessage ?? ''}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
